/**
 * Hebrew held as what it says, rather than as it was typed.
 *
 * The same Hebrew words have several legal encodings, and nothing downstream
 * can tell them apart by looking. שׁ is one character (U+FB2A) or two (ש plus a
 * shin dot); a patach and a ta'am on one letter sit in the order somebody typed
 * them or in the order Unicode sorts them to. Every comparison in the platform
 * — search, dedupe, a digest, a guard checking that letters did not move — is a
 * comparison of bytes, so two spellings of one word are two words to all of
 * them, silently and without an error anywhere.
 *
 * So text is settled ONCE, on the way in, and everything downstream compares
 * settled text against settled text.
 *
 * **This does not change what a text says.** A presentation form and the letter
 * it decomposes to are the same letter — that is what a canonical decomposition
 * means — so normalizing is the opposite of rewriting somebody's words: it is
 * what makes byte-identity mean the same thing as word-identity. The rule that
 * the letters printed must be the letters quoted is kept by this rather than
 * threatened by it.
 *
 * It is also safe for type. Mark ORDER moves under normalization — a patach is
 * combining class 17 and a ta'am 222, so the two swap — and that is only ever
 * a question for the shaper, which sorts the marks it is handed by its own
 * classes before positioning them. `scripts/fonts/font_order.py` asserts it:
 * one cluster spelled canonically, spelled as written, or spelled backwards
 * shapes to the same glyphs at the same offsets. What the order IS load-bearing
 * for is a GPOS rule written into a face, which is matched against the shaper's
 * order and never against stored text.
 */

/**
 * Every mark a Hebrew letter carries, and none of the marks that join or end
 * its words.
 *
 * Taamim (U+0591–U+05AF), nikud and dagesh (U+05B0–U+05BC), meteg, rafe, the
 * shin and sin dots, the upper and lower dots, and qamats qatan.
 *
 * Deliberately absent, because each carries meaning between letters rather than
 * on one: maqaf (U+05BE), which joins words into a phrase; paseq (U+05C0); sof
 * pasuq (U+05C3); and nun hafukha (U+05C6). Stripping those would run words
 * together — `אֶת־הָעָם` is one phrase and loses its join.
 */
const HEBREW_MARKS = /[֑-ׇֽֿׁׂׅׄ]/g

/**
 * The Hebrew presentation forms block.
 *
 * Most of it decomposes canonically, so NFC alone settles it. Eleven do not —
 * the alternative letters (U+FB20–U+FB28), the alternative plus sign, and the
 * alef-lamed ligature — because their decompositions are COMPATIBILITY ones,
 * which NFC leaves exactly where they are.
 */
const PRESENTATION = /[יִ-ﭏ]/g

/**
 * Hebrew settled to one spelling.
 *
 * Compatibility folding is applied to the presentation block ALONE rather than
 * by normalizing the whole string to NFKC. NFKC is not a Hebrew operation: it
 * also rewrites ligatures, width forms, superscripts and much else wherever
 * they appear, so a passage carrying a footnote marker or a European name comes
 * back altered in ways nobody asked for. Folding per character inside one block
 * takes the eleven and touches nothing else — and it stays right when Unicode
 * assigns another codepoint in there, which a hand-written table of eleven
 * would not.
 */
export const settleHebrew = (text: string): string =>
  text.replace(PRESENTATION, (form) => form.normalize('NFKC')).normalize('NFC')

/**
 * The same text with Hebrew pointing removed.
 *
 * Searching vocalized Hebrew is otherwise useless: a reader types the letters
 * and the stored text carries a mark between every one of them, so nothing ever
 * matches. Settled first, because a mark baked into a presentation form is not
 * a mark any pattern can see — text spelled that way would pass through still
 * pointed and land in a column whose whole purpose is to hold letters.
 */
/**
 * The vocal sheva, which is ours and is not in the Hebrew block.
 *
 * The platform writes U+0311 onto a letter to mark a sheva na — a combining
 * inverted breve, which Unicode puts among the general diacritics because other
 * scripts use it too. So no Hebrew range reaches it, and a word carrying one
 * kept it through every strip and landed in the letters column still marked,
 * where a letter search never matched it again.
 *
 * Taken only where it sits on Hebrew, for that same reason: the codepoint is
 * not ours, and stripping it wherever it appears would quietly alter Latin text
 * that means something else by it.
 */
const VOCAL_SHEVA = /(?<=\p{Script=Hebrew})\u0311/gu

export const stripPointing = (text: string): string =>
  settleHebrew(text).replace(HEBREW_MARKS, '').replace(VOCAL_SHEVA, '')

/**
 * Every string inside a value, settled, with the shape left alone.
 *
 * What a book stores is a tree rather than a string, and its text sits at
 * whichever depth the node kind puts it — so a write settles the whole value
 * rather than naming fields, which is what stops the next node kind from being
 * the one nobody remembered. Returns the value unchanged when nothing in it
 * moved, so a caller can tell whether a write is a rewrite.
 */
export function settleDeep<T>(value: T): T {
  if (typeof value === 'string') return settleHebrew(value) as T
  if (Array.isArray(value)) return value.map(settleDeep) as T
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [k, settleDeep(v)]),
    ) as T
  }
  return value
}

/**
 * How many marks the text carries.
 *
 * A count rather than the pattern, because the pattern is global and a global
 * regular expression carries a cursor: shared between callers, `test` and `exec`
 * resume where the last one stopped and answer differently on alternate calls
 * for the same string. `replace` and `match` reset it and are safe, which is
 * exactly what would have made this rare enough to ship and hard to explain.
 *
 * Settled first, for the reason every count here is: a mark baked into a
 * presentation form is not a mark any pattern can see, so a fully pointed text
 * spelled that way counts zero.
 */
export const countMarks = (text: string): number =>
  (settleHebrew(text).match(HEBREW_MARKS) ?? []).length

/** A mark this changed, and what it was taken to be. */
export type Mend = {
  /** Where the mark was, counted in code points over the settled text. */
  at: number
  from: string
  to: string
  says: string
}

const SHIN = 'ש'
const SIN_DOT = 'ׂ'
const HOLAM = 'ֹ'
const HEBREW_LETTER = /\p{Script=Hebrew}/u

/**
 * A mark repaired where the one on the page cannot be read at all.
 *
 * This is CLEANUP rather than a change of meaning, and the distinction is the
 * whole license for it. A sin dot on a letter that is not shin is not a reading
 * somebody chose and this overrules — it is a form no edition sets, which the
 * platform already calls `impossible` in `pointingFaults`. There is no sense on
 * the page to preserve, so repairing it takes nothing away from a reader.
 *
 * That is also the boundary. A mark is mended only where the form as written is
 * impossible AND exactly one reading explains it; anything merely unusual, or
 * impossible in more than one way, is reported and left alone. A rule that
 * mended an unusual form would be editing somebody's pointing, and this would
 * stop being cleanup the moment one was added.
 *
 * **A sin dot on a letter that is not shin.** It and a holam sit above and to
 * the left, so they are struck for each other constantly; a sin dot means
 * nothing anywhere but on a shin; and a holam is the only mark written where it
 * was put. One impossible form, one explanation.
 *
 * The shin dot is deliberately absent. It sits above and to the RIGHT, where
 * nothing else is written — so a wrong one is impossible without being
 * explained by anything, and there is no single mark it was struck for.
 * `pointingFaults` reports both either way.
 *
 * What was mended is returned beside the text. A caller may discard it — this
 * is cleanup and nobody needs telling that an unreadable mark was tidied — but
 * an importer reporting what a source needed is the reason it is here.
 */
export function mendPointing(text: string): { text: string; mends: Mend[] } {
  const chars = [...settleHebrew(text)]
  const mends: Mend[] = []
  let base = ''

  const out = chars.map((ch, at) => {
    if (HEBREW_LETTER.test(ch) && !/\p{Mark}/u.test(ch)) {
      base = ch
      return ch
    }
    if (ch === SIN_DOT && base && base !== SHIN) {
      mends.push({
        at,
        from: SIN_DOT,
        to: HOLAM,
        says: `a sin dot on ${base}, which only shin takes — read as a holam`,
      })
      return HOLAM
    }
    return ch
  })

  return { text: out.join(''), mends }
}

/**
 * Every string inside a value, settled and mended, with the shape left alone.
 *
 * What a write stores is a tree rather than a string, so this is `settleDeep`
 * with the repair of unreadable marks folded in — the two belong together on
 * the way in, because a book is stored once and read for ever after.
 *
 * The mends are gathered from the whole value rather than per string, so a
 * caller can say what a passage needed without walking it a second time.
 */
export function mendDeep<T>(value: T): { value: T; mends: Mend[] } {
  const mends: Mend[] = []
  const walk = (v: unknown): unknown => {
    if (typeof v === 'string') {
      const done = mendPointing(v)
      mends.push(...done.mends)
      return done.text
    }
    if (Array.isArray(v)) return v.map(walk)
    if (v && typeof v === 'object') {
      return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, walk(x)]))
    }
    return v
  }
  return { value: walk(value) as T, mends }
}
